import { useEffect, useMemo, useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import { MapPin, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import apiService from "@/services/apiService";

/** Astrafin HQ (Bengaluru) — fallback center when no pins. */
const DEFAULT_POSITION = { lat: 12.9121, lng: 77.6446 };

const apiKey = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "").trim();
const mapId =
  String(import.meta.env.VITE_GOOGLE_MAPS_MAP_ID ?? "").trim() || "DEMO_MAP_ID";

export type MapLocationPin = {
  invoiceId: string | number;
  invoiceNumber?: string | null;
  rsNo?: string | null;
  latitude: number;
  longitude: number;
  customerId?: string | null;
  customerName: string;
  companyName?: string | null;
  companyLogo?: string | null;
  purchaseOrderCode?: string | null;
};

function extractLocations(response: unknown): MapLocationPin[] {
  const root = (response as { data?: unknown })?.data ?? response;
  const list =
    (root as { locations?: unknown })?.locations ??
    (Array.isArray(root) ? root : []);
  if (!Array.isArray(list)) return [];

  return list
    .map((row, index) => {
      const r = row as Record<string, unknown>;
      const latitude = Number(r.latitude ?? r.lat);
      const longitude = Number(r.longitude ?? r.lng ?? r.lon);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      const customerName = String(
        r.customerName ??
          r.customer_name ??
          r.companyName ??
          r.company_name ??
          r.entityName ??
          r.entity_name ??
          "Unknown"
      ).trim();
      const rsNoRaw = r.rsNo ?? r.rs_no ?? r.rsNumber ?? r.rentalScheduleNo;
      const rsNo =
        rsNoRaw != null && String(rsNoRaw).trim() !== ""
          ? String(rsNoRaw).trim()
          : null;
      const logoRaw =
        r.companyLogo ?? r.company_logo ?? r.logoUrl ?? r.logo_url ?? null;
      const companyLogo =
        logoRaw != null && String(logoRaw).trim() !== ""
          ? String(logoRaw).trim()
          : null;
      return {
        invoiceId: (r.invoiceId ?? r.invoice_id ?? index) as string | number,
        invoiceNumber: (r.invoiceNumber ?? r.invoice_number ?? null) as
          | string
          | null,
        rsNo,
        latitude,
        longitude,
        customerId: (r.customerId ?? r.customer_id ?? null) as string | null,
        customerName: customerName || "Unknown",
        companyName: (r.companyName ?? r.company_name ?? null) as string | null,
        companyLogo,
        purchaseOrderCode: (r.purchaseOrderCode ??
          r.purchase_order_code ??
          null) as string | null,
      } satisfies MapLocationPin;
    })
    .filter((p): p is MapLocationPin => p != null);
}

function MapBoundsFitter({ pins }: { pins: MapLocationPin[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || pins.length === 0) return;

    if (pins.length === 1) {
      map.setCenter({ lat: pins[0].latitude, lng: pins[0].longitude });
      map.setZoom(12);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    for (const pin of pins) {
      bounds.extend({ lat: pin.latitude, lng: pin.longitude });
    }
    map.fitBounds(bounds, 56);
  }, [map, pins]);

  return null;
}

export function AdminDashboardMap() {
  const [pins, setPins] = useState<MapLocationPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPin, setSelectedPin] = useState<MapLocationPin | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/admin-dashboard-map-locations"
        );
        if (cancelled) return;
        setPins(extractLocations(response));
      } catch (err) {
        console.error("Failed to load map locations:", err);
        if (!cancelled) {
          setPins([]);
          setError("Failed to load map locations");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const center = useMemo(() => {
    if (pins.length === 0) return DEFAULT_POSITION;
    const lat =
      pins.reduce((sum, p) => sum + p.latitude, 0) / Math.max(pins.length, 1);
    const lng =
      pins.reduce((sum, p) => sum + p.longitude, 0) / Math.max(pins.length, 1);
    return { lat, lng };
  }, [pins]);

  return (
    <Card className="overflow-hidden border">
      <div className="flex items-start justify-between gap-3 border-b px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Geographic view
          </p>
          <h3 className="mt-0.5 text-base font-semibold text-foreground">
            Portfolio Map
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {loading
              ? "Loading locations…"
              : pins.length > 0
                ? `${pins.length} invoice location${pins.length === 1 ? "" : "s"}`
                : "No invoice locations with coordinates"}
          </p>
        </div>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <MapPin className="h-4 w-4" />
        </div>
      </div>

      <div className="relative h-[360px] w-full bg-muted/30 sm:h-[420px]">
        {loading ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Loading map locations…
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
            {error}
          </div>
        ) : (
          <APIProvider apiKey={apiKey} region="IN">
            <Map
              defaultCenter={center}
              defaultZoom={pins.length ? 6 : 12}
              mapId={mapId}
              gestureHandling="greedy"
              disableDefaultUI={false}
              style={{ width: "100%", height: "100%" }}
            >
              <MapBoundsFitter pins={pins} />
              {pins.map((pin) => (
                <AdvancedMarker
                  key={String(pin.invoiceId)}
                  position={{ lat: pin.latitude, lng: pin.longitude }}
                  title={pin.customerName}
                  onClick={() => setSelectedPin(pin)}
                >
                  <Pin
                    background="#EA4335"
                    borderColor="#C5221F"
                    glyphColor="#ffffff"
                  />
                </AdvancedMarker>
              ))}

              {selectedPin && (
                <InfoWindow
                  position={{
                    lat: selectedPin.latitude,
                    lng: selectedPin.longitude,
                  }}
                  onCloseClick={() => setSelectedPin(null)}
                  pixelOffset={[0, -36]}
                  maxWidth={280}
                  className="admin-dashboard-map-iw"
                  headerContent={
                    <div className="flex max-w-[220px] items-center gap-2 pr-1">
                      {selectedPin.companyLogo ? (
                        <img
                          src={selectedPin.companyLogo}
                          alt=""
                          className="h-7 w-7 shrink-0 rounded-md border border-slate-200 bg-white object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-500">
                          {(selectedPin.customerName || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="min-w-0 truncate text-[13px] font-semibold text-slate-900">
                        {selectedPin.customerName}
                      </span>
                    </div>
                  }
                >
                  <p className="m-0 text-[11px] leading-tight text-slate-600">
                    RS No:{" "}
                    <span className="font-medium text-slate-800">
                      {selectedPin.rsNo?.trim() ? selectedPin.rsNo : "—"}
                    </span>
                  </p>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        )}
      </div>
    </Card>
  );
}
